import React, { lazy, Suspense } from "react";
import { useParams } from "react-router-dom";
import SideMenu from "../components/SideMenu";
import { useTour } from "../hooks/useTour";
import "../styles/HelpDocs.css";

const DemoFindVenues = lazy(() => import("../docs-demos/DemoFindVenues"));
const DemoParameters = lazy(() => import("../docs-demos/DemoParameters"));
const DemoPreferences = lazy(() => import("../docs-demos/DemoPreferences"));
const DemoDistance = lazy(() => import("../docs-demos/DemoDistance"));
const DemoDashboard = lazy(() => import("../docs-demos/DemoDashboard"));

export default function HelpDocs() {
  const { userId } = useParams();

  const TOUR_STEPS = [
    {
      element: ".help-section:first-of-type",
      popover: {
        title: "Welcome!",
        description:
          "This short walkthrough shows how each piece of the app works. Click Next to begin.",
      },
    },

    {
      element: ".help-section:first-of-type .demo-frame",
      popover: {
        description: "Each purple card contains an interactive sandbox.",
      },
    },

    {
      element: "#fv-map-wrap",
      popover: {
        title: "Interactive map",
        description:
          "Pan and zoom around California to browse venues. You can try it yourself during this demo.",
      },
    },

    {
      element: "#fv-zoom-in",
      popover: {
        description: "Use the + / – buttons to zoom.",
      },
    },

    {
      element: ".leaflet-marker-icon",
      popover: {
        description: "Click a pin to select a venue.",
      },
    },

    {
      element: ".tooltip-content",
      popover: {
        description:
          "Hover to see a photo, title, and short description of the venue.",
      },
    },

    {
      element: ".map-legend",
      popover: {
        description:
          "Pink = selected · Blue = unselected. That’s it. Let's move on to the next step!",
      },
    },

    // STEP 2

    {
      element: ".help-section:nth-of-type(2)", // highlight the card
      popover: {
        title: "Step 2 · Filter by Tags",
        description:
          "Instead of using the map to pick a venue, you can use our tag filtering method. Let’s pick the venue styles you love.",
      },
    },
    {
      element: "#pc-frame", // faux browser frame
      popover: { description: "Here’s the page you will encounter." },
    },
    {
      element: "#tm-selector",
      popover: {
        description: "Click to open the tag list. Try clicking it now.",
      },
    },
    {
      element: "#tm-selector.open", // opened dropdown
      popover: {
        description: "Here you can pick as many tags as you would like.",
      },
    },
    {
      element: "#tm-pill-garden",
      popover: {
        description:
          "Let’s choose “Garden” to see an example. Try clicking it!",
      },
    },
    {
      element: "#tm-venues",
      popover: {
        description:
          "Here we can see all the matching venues that fall under the 'Garden' category.",
      },
    },
    {
      element: "#tm-venue-card",
      popover: {
        description:
          "These cards are clickable, allowing you to select / deselect venues. Try clicking the first one!",
      },
    },
    {
      element: "#tm-venue-card.selected",
      popover: {
        description:
          "Selected venues turn purple and stay listed. Once you have picked all the venues, you would see a button to continue to the next step, or skip to our matching process.",
      },
    },

    // Step 3

    {
      element: ".help-section:nth-of-type(3)",
      popover: {
        title: "Step 3 · Limit by distance",
        description:
          "Optional: keep all matching venues within a driveable radius.",
      },
    },

    {
      element: "#dist-root",
      popover: { description: "This page appears after the Tags step." },
    },

    {
      element: "#dist-controls",
      popover: { description: "Pick a radius from your current location." },
    },

    {
      element: "#dist-custom",
      popover: { description: "Or type a custom city / address here." },
    },

    {
      element: "#dist-map",
      popover: {
        description:
          "Map works just like before — click pins to select or deselect venues.",
      },
    },

    // Step 4

    {
      element: ".help-section:nth-of-type(4)",
      popover: {
        title: "Step 4 · Choose weather preferences",
        description: "Fine-tune the weather you want for your big day.",
      },
    },
    { element: "#pref-frame", popover: { description: "Live sandbox #3." } },
    {
      element: "#pref-root",
      popover: { description: "First you’ll fill out this form." },
    },
    {
      element: "#pref-form",
      popover: { description: "Pick as many or as few options as you like." },
    },
    {
      element: "#pref-group-temp",
      popover: {
        description: "At least ONE weather variable must be selected.",
      },
    },
    {
      element: "#pref-group-date",
      popover: { description: "Set your overall date range here." },
    },
    {
      element: "#pref-nav-form",
      popover: { description: "Hit “See Dates” to generate matches." },
    },

    {
      element: "#pref-results-root",
      popover: { description: "Results page appears below." },
    },
    {
      element: "#pref-results-intro",
      popover: {
        description: "Quick instructions, a legend, and your chosen prefs.",
      },
    },
    {
      element: "#pref-legend",
      popover: { description: "Colour coding for match quality." },
    },
    {
      element: "#pref-summary",
      popover: { description: "Your selected preferences at a glance." },
    },
    {
      element: "#pref-venues",
      popover: { description: "Each venue shows a calendar of matches." },
    },
    {
      element: "#pref-venue-card",
      popover: { description: "Click a date to preview details." },
    },
    {
      element: "#pref-date-panel",
      popover: {
        description: "Details appear here — click any date again to remove.",
      },
    },
    {
      element: "#pref-nav-results",
      popover: {
        description: "Edit preferences or continue to your dashboard.",
      },
    },
  ];

  const [startTour] = useTour(TOUR_STEPS);

  return (
    <div className="help-page">
      {/* left column */}
      <div className="side-menu">
        <SideMenu userId={userId} />
      </div>

      {/* right column */}
      <div className="help-content">
        <header className="help-header">
          <h1 className="help-title">Help&nbsp;&amp;&nbsp;Documentation</h1>
        </header>

        <button
            className="btn btn-secondary start-tour-btn"
            onClick={startTour}
          >
            ▶ Start guided tour
          </button>

        <section className="help-section">
          <h2 className="help-section-title">1 · Picking a Venue</h2>
          <p className="help-section-desc">
            Start on the map, click a pin (
            <strong style={{ color: "#E578FF" }}>
              pink&nbsp;=&nbsp;selected
            </strong>
            ). From here you can continue with our filters (steps
            2&nbsp;&amp;&nbsp;3) or skip straight to matching (step 4).
          </p>
          <Suspense fallback={<p className="demo-loading">Loading…</p>}>
            <DemoFindVenues />
          </Suspense>
        </section>

        <section className="help-section">
          <h2 className="help-section-title">2 · [Optional] Picking a Venue by Categories</h2>
          <p className="help-section-desc">
            Narrow results with tag pills to focus on venue style, scenery, or
            amenities.
          </p>
          <Suspense fallback={<p className="demo-loading">Loading…</p>}>
            <DemoParameters />
          </Suspense>
        </section>

        <section className="help-section">
          <h2 className="help-section-title">
            3 · [Optional] Picking a Venue by Distance
          </h2>
          <p className="help-section-desc">
            Keep everything within a comfortable drive by choosing a radius from
            your current or custom location. 
          </p>
          <Suspense fallback={<p className="demo-loading">Loading…</p>}>
            <DemoDistance />
          </Suspense>
        </section>

        <section className="help-section">
          <h2 className="help-section-title">
            4 · Choosing Weather Preferences
          </h2>
          <p className="help-section-desc">
            Pick your ideal temperature range, wind speed, sunset time, etc.,
            then hit “See Dates” to fetch the best matches.
          </p>
          <Suspense fallback={<p className="demo-loading">Loading…</p>}>
            <DemoPreferences />
          </Suspense>
        </section>

        <section className="help-section">
          <h2 className="help-section-title">5 · Reviewing Your Dashboard</h2>
          <p className="help-section-desc">
            After saving a venue + date, your Couple Dashboard shows an
            expandable card for each selection. Click the card to reveal hourly
            charts &amp; quick insights, or the trash-can to remove it.
          </p>
          <Suspense fallback={<p className="demo-loading">Loading…</p>}>
            <DemoDashboard />
          </Suspense>
        </section>

        <footer className="help-footer">
          Need more help?&nbsp;
          <a href="mailto:support@bestday2marry.com">
            support@bestday2marry.com
          </a>
        </footer>
      </div>
    </div>
  );
}
